import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components/macro';

import { ReactComponent as GoToTicketIcon } from 'assets/images/go-to-ticket.svg';
import { ReactComponent as AssignIcon } from 'assets/images/assign.svg';
import { ReactComponent as AssignTicketLoading } from 'assets/images/assign-loading.svg';

import headArrow from 'assets/images/headArrow.svg';
import copyIcon from 'assets/images/copyIcon.svg';
import headArrowDown from 'assets/images/headArrowDown.svg';
import { Box, CircularProgress, Fab } from '@material-ui/core';

import {
  isLoadingTicketsSelector,
  isManualSiLoadingMapSelector,
  justChangedAndFromWsTicketIdsSelector,
  workdeskTicketsSelector,
} from 'store/selectors/workdesk-tickets.selector';
import { createSelector } from '@reduxjs/toolkit';
import { useSelector, useDispatch } from 'react-redux';
import { useOutsideAlerter } from 'custom-hooks';
import {
  alertsActions,
  assignUnassignActions,
  showOverlayActions,
  ticketGroupTableActions,
  workdeskFilterActions,
} from 'store/actions';
import { workdeskTicketsActions } from 'store/actions/workdesk-tickets.actions';
import { history } from 'store/helpers';
import {
  ticketLabelColorMap,
  WorkdeskTickets,
} from 'store/models/workdesk-tickets/WorkdeskTickets';
import copy from 'copy-to-clipboard';
import {
  ticketGroupTableSelectedIdSelector,
  workdeskFilterSelector,
} from 'store/selectors';

import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { WorkdeskFilterSortBy } from 'store/models/WorkdeskFilter';
import {
  Check,
  CheckCircleOutlineRounded,
  Close,
  ErrorOutlineRounded,
  KeyboardArrowDownRounded,
  KeyboardArrowUpRounded,
  Sort,
  YoutubeSearchedForRounded,
} from '@material-ui/icons';
import Tooltip from '@material-ui/core/Tooltip';
import { recentTicketIdSelector } from 'store/selectors/ticket.selector';
import SelectAllCheckbox from '../SelectAllCheckbox';
import BulkActionBar from '../BulkActionBar';
import { manualSiActions } from 'store/actions/manual-si.actions';
import GmailLabels, { SGmailLabel } from './GmailLabels';
import envCheckers from 'utils/envCheckers';
import EachWorkdeskTicket from 'store/models/EachWorkdeskTicket';
import { WorkdeskScrollHelper } from 'store/helpers/WorkdeskScrollHelper';
import { dashIfEmpty } from 'utils/empty';
import { BottomScrollListener } from 'react-bottom-scroll-listener';
import workdeskColumns, {
  EachWorkdeskColumn,
} from './workdeskColumns';
import {
  canOpenTicketSelector,
  canSelectTicketSelector,
  showAssignUnassignButtonSelector,
  showMarkSiNonSiSelector,
  statusCountForTicketTypeSelector,
  workdeskFilterHasAnySearchValuesSelector,
  workdeskFilterWithPrevSelector,
} from 'store/selectors/workdesk-filter.selector';
import { wsCurStatusInboxesLatestReceivedTimeWithDataSelector } from 'store/selectors/workdesk-filter.selector';
import { ROWS_PER_STATUS } from 'config/workdesk-load';
import {
  EachTicketGroup,
  EachWorkdeskTicketToShowI,
  LoadWorkdeskTicketsParams,
} from 'store/models/workdesk-tickets/workdesk-tickets.interface';
import { TimeTrackingStatus } from 'components/common/DurationPanel';

interface TicketGroupTableProps {
  ticketGroup: EachTicketGroup;
}

interface Choice {
  title: string;
  label: string;
  ref: any; // come back to change this later
}

const isAssigningTicketMapSelector = createSelector(
  workdeskTicketsSelector,
  (wdTickets) => {
    return wdTickets.assigningTicketIdMap;
  },
);

const TicketGroupTable: React.FC<TicketGroupTableProps> = ({
  ticketGroup,
}) => {
  const {
    statusesStartAt,
    sortBy,
    selectedStatus,
    selectedTicketType,
  } = useSelector(workdeskFilterSelector);

  const canSelectTicket = useSelector(canSelectTicketSelector);
  const canOpenTicket = useSelector(canOpenTicketSelector);
  const { assign, unassign } = useSelector(
    showAssignUnassignButtonSelector,
  );
  const canAssignOrUnassignTicket = useMemo(
    () => assign || unassign,
    [assign, unassign],
  );
  const { markSi, markNonSi } = useSelector(showMarkSiNonSiSelector);
  const canMarkSiTicket = useMemo(() => markSi || markNonSi, [
    markSi,
    markNonSi,
  ]);

  const selectedId = useSelector(ticketGroupTableSelectedIdSelector);
  useEffect(() => {
    console.debug(`selectedId:`, selectedId);
  }, [selectedId]);
  const sortByMap = useMemo(
    () =>
      sortBy.reduce(
        (acc, by) => ({ ...acc, [by.field]: by.dir }),
        {},
      ),
    [sortBy],
  );

  const workdeskColumnsSortByMap = useMemo(
    () =>
      workdeskColumns.reduce((acc, col) => {
        if (col.sortable) {
          const field = col.sortByField || col.dataKey;
          acc[field] = sortByMap[field] || 'DESC';
        }
        return acc;
      }, {}),
    [sortByMap],
  );
  const dispatch = useDispatch();
  const isAssignLoadingTicketIdMap = useSelector(
    isAssigningTicketMapSelector,
  );
  const recentTicketId = useSelector(recentTicketIdSelector);

  const allTicketIdsInGroup = useMemo(
    () => ticketGroup?.tickets.map((t) => t.id) || [],
    [ticketGroup],
  );

  const hasRecentTicketId = useMemo(
    () =>
      recentTicketId !== undefined &&
      recentTicketId !== null &&
      allTicketIdsInGroup.includes(recentTicketId),
    [recentTicketId, allTicketIdsInGroup],
  );

  const manualSiLoadingIdMap = useSelector(
    isManualSiLoadingMapSelector,
  );
  const [showTicket, setShowTicket] = useState<{
    [id: number]: boolean;
  }>({});
  const [showMoreInfos, setShowMoreInfos] = useState<{
    [id: number]: boolean;
  }>({});
  const showDropdownRef = useRef(null);

  // scroll into view
  const rowRefs = useMemo(() => {
    if (!ticketGroup || !ticketGroup.tickets) return {};
    return ticketGroup.tickets.reduce(
      (acc, ticket) => ({ ...acc, [ticket.id]: React.createRef() }),
      {},
    );
  }, [ticketGroup]);

  const [dropdownId, setDropdownId] = useState<string>('');

  useOutsideAlerter(showDropdownRef, () => {
    setDropdownId('');
  });

  const handleClickTicketRow = (id: string) => {
    setShowMoreInfos({ ...showMoreInfos, [id]: !showMoreInfos[id] });
  };

  const collapseAllShowMoreInfos = () => {
    setShowMoreInfos({});
  };
  const handleThreePoint = (id: string) => {
    setDropdownId(id);
    console.debug('dropdown status : ', dropdownId);
  };

  const handleToggleShowGroup = (id: string) => {
    const newStatus = !showTicket[id];
    setShowTicket({ ...showTicket, [id]: newStatus });

    // auto collase the children if toggle to collapse the group
    if (!newStatus) {
      collapseAllShowMoreInfos();
    }
  };
  const renderNoTickets = () => {
    if (!ticketGroup.isLoading) {
      return <SNoTickets>No tickets found</SNoTickets>;
    }
  };

  const handleAssign = (ticketId: string) => {
    console.debug('handleAssign', ticketId);
    // comment out for rolling out expand detail
    dispatch(assignUnassignActions.postAssignTicket(ticketId));
  };

  const handleDirect = (ticket: EachWorkdeskTicketToShowI) => {
    const { id } = ticket;
    if (!canOpenTicket) return;
    if (!ticket.assigneeId) {
      // comment below out if want to assign on open
      // dispatch(workdeskTicketsActions.postAssignTickets(id));
    } else {
      // only clear if doesn't do post assign
      // TODO: handle when doesn't change page (rn, clearJustChangedTicketId doesn't seem to behave as expect)
      dispatch(workdeskTicketsActions.clearJustChangedTicketId());
    }
    if (!isHoldingCtrl) {
      history.push(`/main/${id}`);
    } else {
      const win = window.open(
        `/main/${id}`,
        '_blank',
        'noopener,noreferrer',
      );
      if (win) win.focus();
    }
  };

  /****************** start: check for holding ctrl or cmd (TODO: find proper place to put this piece of code) ********************/
  const [isHoldingCtrl, setIsHoldingCtrl] = useState(false);

  const _handleKeydown = useRef((e) => {
    if (e.metaKey) {
      // check if holding ctrls
      setIsHoldingCtrl(true);
    }
  });

  const _handleKeyUp = useRef((e) => {
    setIsHoldingCtrl(false);
  });

  useEffect(() => {
    const handlerKeydown = _handleKeydown.current;
    const handlerKeyup = _handleKeyUp.current;
    document.addEventListener('keydown', handlerKeydown);
    document.addEventListener('keyup', handlerKeyup);
    return () => {
      document.removeEventListener('keydown', handlerKeydown);
      document.removeEventListener('keyup', handlerKeyup);
    };
  }, []);

  useEffect(() => {
    console.debug(`isHoldingCtrl: `, isHoldingCtrl);
  }, [isHoldingCtrl]);
  /****************** end: check for holding ctrl or cmd  ********************/

  const handleonClickUnassign = (ticketId) => {
    // unassign from ... (three dots button)
    dispatch(
      showOverlayActions.show('Note', {
        ticketId,
        status: TimeTrackingStatus.unassigned,
        durationInSeconds: 0,
      }),
    );
    // dispatch(assignUnassignActions.postUnassignTicket(ticketId));
  };

  const getDropdownChoices = useMemo(
    () => (assigneeId: number | null, si): Choice[] => {
      // TODO: come back to change type from any to somethign appro.
      let choices: { title: string; label: string; ref: any }[] = [];
      if (canOpenTicket) {
        choices = choices.concat({
          title: 'GO_TO_TICKET',
          label: 'Go to ticket',
          ref: <GoToTicketIcon />,
        });
      }
      if (canAssignOrUnassignTicket) {
        choices = choices.concat([
          {
            title: assigneeId === null ? 'ASSIGN' : 'UNASSIGN',
            label: assigneeId === null ? 'Assign To Me' : 'Unassign',
            ref: <AssignIcon />,
          },
        ]);
      }
      if (canMarkSiTicket) {
        choices = choices.concat([
          {
            title: 'TOGGLE_SI',
            label: si ? 'Mark Non SI' : 'Mark SI',
            ref: si ? <SMarkNSiDropIcon /> : <SMarkSiDropIcon />,
          },
        ]);
      }
      return choices;
    },
    [canOpenTicket, canMarkSiTicket, canAssignOrUnassignTicket],
  );

  const handleCopy = (textCopy) => {
    copy(textCopy);
    const message = 'copied to clipboard';
    dispatch(alertsActions.success(message));
    setTimeout(() => {
      dispatch(alertsActions.clear({ message }));
    }, 5000);
  };
  const handleChoiceAction = (title, ticket) => {
    console.debug('title', title);
    const { id } = ticket;
    if (!id) throw Error(`id shouldn't be missing`);
    switch (title) {
      case 'GO_TO_TICKET':
        return handleDirect(ticket);
      case 'ASSIGN':
        ticket.status !== 'PREPROCESSING' &&
          ticket.status !== 'SI_RECEIVED' &&
          handleAssign(id);
        break;
      case 'UNASSIGN':
        handleonClickUnassign(id);
        break;
      case 'TOGGLE_SI':
        handleSINonSI(!ticket.si, id);
        break;
      default:
        break;
    }
  };

  const renderLabelWithSortDir = (col: EachWorkdeskColumn) => {
    const field = col.sortByField || col.dataKey;
    let IconInside;

    let shouldRenderSort = col.sortable && field in sortByMap;
    let dir;
    if (shouldRenderSort) {
      //   TODO: consider reinitial WorkdeskFilterSortBy and save only every time sortBy changes
      dir = new WorkdeskFilterSortBy(sortBy).getSortDirOfField(field);
      if (!dir) {
        shouldRenderSort = false;
      }
      // arrow icon means currently sorted by that column
      // sortdir icon means other columns that can be sorted by
      switch (dir) {
        case 'DESC':
          IconInside = ArrowDropDownIcon;
          break;
        case 'ASC':
          IconInside = ArrowDropUpIcon;
          break;
        default:
          shouldRenderSort = false;
          break;
      }
    }

    return (
      <SSortLabelWithDir
        className={col.sortable ? 'clickable' : ''}
        onClick={() =>
          col.sortable ? handleToggleSort(field) : null
        }
      >
        <SSortLabel>{col.label}</SSortLabel>
        {col.sortable && (
          <SSortDirAndRank>
            <SSortDir>
              {shouldRenderSort ? (
                <Tooltip title={dir}>
                  <IconInside />
                </Tooltip>
              ) : (
                <SSortUpdown fontSize="small" />
              )}
            </SSortDir>
          </SSortDirAndRank>
        )}
      </SSortLabelWithDir>
    );
  };

  /**
   * if in the current sort by -> switch direction
   * if not -> use the DESC
   * @param field
   */
  const handleToggleSort = (field: string) => {
    const curDir = workdeskColumnsSortByMap[field];
    if (!sortBy || !sortBy.length) throw Error(`need sortBy length`);
    const firstSortByField = sortBy[0].field;
    const dir =
      field !== firstSortByField
        ? curDir
        : WorkdeskFilterSortBy.getTheOppositeSortDir(curDir);
    dispatch(workdeskFilterActions.toggleSortByOfField(field, dir));
    dispatch(workdeskTicketsActions.loadWorkdeskTickets());
  };

  const [showBackToTopBtn, setShowBackToTopBtn] = useState(false);

  const isScrollable: boolean = useMemo(
    () =>
      ticketGroup && ticketGroup.tickets.length > 16 ? true : false,
    [ticketGroup],
  );

  /**
   * hit bottom -> fetch
   * @param event
   */
  const handleScroll = async (event) => {
    // what happen if at the center
    // console.debug(
    //   `handle scroll`,
    //   event.target.scrollHeight, // fixed until update tickets
    //   event.target.scrollTop, // increase
    //   event.target.scrollHeight - event.target.scrollTop, // decrease
    //   event.target.clientHeight, // fixed forever
    // );
    const passedTop = WorkdeskScrollHelper.isPassedTop(event);
    // const bottom = WorkdeskScrollHelper.isAtBottom(event);
    if (passedTop && !showBackToTopBtn) {
      setShowBackToTopBtn(true);
    } else if (!passedTop && showBackToTopBtn) {
      setShowBackToTopBtn(false);
    }
  };

  const isLoadingTickets = useSelector(isLoadingTicketsSelector);
  const justChangedAndFromWsTicketIds = useSelector(
    justChangedAndFromWsTicketIdsSelector,
  );

  const wdFilterWithPrev = useSelector(
    workdeskFilterWithPrevSelector,
  );

  const hasAnySearch = useSelector(
    workdeskFilterHasAnySearchValuesSelector,
  );

  const numTicket = useSelector(statusCountForTicketTypeSelector);
  const statusNumTicket = useMemo(
    () => numTicket && selectedStatus && numTicket[selectedStatus],
    [numTicket, selectedStatus],
  );

  const [isLoadingForScroll, setIsLoadingForScroll] = useState(false);
  /**
   * TODO: check the case where got a lot if update from web socket
   * will there be scroll issue?
   */
  const hitBottomCallback = async () => {
    if (!isLoadingTickets && !isLoadingForScroll) {
      setIsLoadingForScroll(true);
      const totalCurrentLenght = ticketGroup.tickets.length;
      if (
        !hasAnySearch &&
        statusNumTicket !== undefined &&
        statusNumTicket <= totalCurrentLenght
      ) {
        setIsLoadingForScroll(false);
        return;
      }
      const firstJustChangeOrWsIndex = ticketGroup.tickets.findIndex(
        (ticket) => justChangedAndFromWsTicketIds.includes(ticket.id),
      );
      let tempChanged = ticketGroup.tickets.filter((ticket) => {
        return justChangedAndFromWsTicketIds.includes(ticket.id);
      });
      const lastJustChangeOrWsIndex = tempChanged.length - 1;

      const newStartAt =
        firstJustChangeOrWsIndex > -1
          ? firstJustChangeOrWsIndex
          : totalCurrentLenght;
      const lastIndex = ticketGroup.tickets.length - 1;

      console.debug(
        `bottom! newStartAt:`,
        newStartAt,
        'lastJustChangeOrWsIndex: s',
        lastJustChangeOrWsIndex,
        {
          justChangedAndFromWsTicketIds,
          firstJustChangeOrWsIndex,
          lastJustChangeOrWsIndex,
          tIds: ticketGroup.tickets.map((t) => t.id),
        },
      );
      // trigger fetch more data
      dispatch(
        workdeskFilterActions.changeStartAtForStatus(
          ticketGroup.status,
          newStartAt,
        ),
      );
      let newStartAtLoop = newStartAt;
      let ticketsLength = undefined;
      let params = WorkdeskTickets.workdeskFilterToLoadParams(
        wdFilterWithPrev,
      ) as LoadWorkdeskTicketsParams;
      let countLoop = 0;
      const maxLoop = 5;
      let newParams = { ...params };
      do {
        newParams = {
          ...newParams,
          startAt: newStartAtLoop,
        };
        const tickets = await dispatch(
          workdeskTicketsActions.loadWorkdeskTickets(
            false,
            newParams,
            undefined,
            true,
          ),
        );
        ticketsLength = tickets?.length ?? 0;

        countLoop += 1;
        console.debug(
          `bottom! load tickets with params: startAt: ${newParams.startAt}`,
          {
            newStartAtLoop,
            ticketsLength,
            lastJustChangeOrWsIndex,
            totalCurrentLenght,
            countLoop,
            lastIndex,
          },
        );
        newStartAtLoop += ticketsLength ?? 0;
        if (countLoop > maxLoop) {
          break;
        }
      } while (
        newStartAtLoop <= lastJustChangeOrWsIndex &&
        (ticketsLength === undefined || ticketsLength !== 0)
      );

      if (ticketsLength !== 0 && lastJustChangeOrWsIndex > -1) {
        // fetch new batch
        newParams = {
          ...newParams,
          startAt: totalCurrentLenght,
        };
        console.debug(
          `bottom! fetch new batch: startAt: ${newParams.startAt}`,
        );
        await dispatch(
          workdeskTicketsActions.loadWorkdeskTickets(
            false,
            newParams,
            undefined,
            true,
          ),
        );
        setIsLoadingForScroll(false);
      } else {
        setIsLoadingForScroll(false);
      }
    } else {
      console.debug(`bottom! still loading`);
    }
  };

  /**
   * scroll
   */
  const handleFindRecentTicketrow = () => {
    if (!recentTicketId || !(recentTicketId in rowRefs)) return;
    rowRefs[recentTicketId].current.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'end',
    });
  };

  const topTicketId: string | null = useMemo(
    () =>
      ticketGroup && ticketGroup.tickets.length > 0
        ? ticketGroup.tickets[0].id
        : null,
    [ticketGroup],
  );
  const bottomTicketId: string | null = useMemo(
    () =>
      ticketGroup && ticketGroup.tickets.length > 0
        ? ticketGroup.tickets[ticketGroup.tickets.length - 1].id
        : null,
    [ticketGroup],
  );

  const scrollToTop = () => {
    if (!topTicketId || !(topTicketId in rowRefs)) return;
    rowRefs[topTicketId].current.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });
  };

  const scrollToBottom = () => {
    if (!bottomTicketId || !(bottomTicketId in rowRefs)) return;
    rowRefs[bottomTicketId].current.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'end',
    });
  };

  const renderDropdown = (
    ticket: EachWorkdeskTicketToShowI,
    numIndex,
    numTicket,
  ) => {
    const { assigneeId, si } = ticket;
    return (
      <SMoreDropdown
        ref={showDropdownRef}
        className={`${numIndex - numTicket <= 5 ? 'last' : ''}
          ${numIndex <= 19 ? 'lessTicket' : ''}`}
      >
        {getDropdownChoices(assigneeId, si).map((choice, index) => [
          choice && (
            <SEachChoice
              className={`order${index + 1} `}
              key={choice.title}
              onClick={() => handleChoiceAction(choice.title, ticket)}
            >
              <STextChoice>
                <SDropdownIcon>
                  {/* {if(subTitle&&)} */}
                  {choice?.ref}
                </SDropdownIcon>
                {choice.label}
              </STextChoice>
              {getDropdownChoices.length !== index && (
                <SDropdownLine />
              )}
            </SEachChoice>
          ),
        ])}
      </SMoreDropdown>
    );
  };
  const handleSINonSI = (si: boolean, ticketId: string) => {
    dispatch(manualSiActions.postManualSi(ticketId, si));
  };
  const handlePreviewEmailBody = (e, id: string) => {
    // dispatch(
    //   alertsActions.error(
    //     `This is a mocked body html for now. Waiting for the backend to do POST ticket/body-htmls { ids: string[] }`,
    //   ),
    // );

    // need this stop propagation, so it won't also toggle expand the row
    e.stopPropagation();
    dispatch(workdeskTicketsActions.setPreviewingBodyHtmlId(id));
    dispatch(showOverlayActions.show('PreviewEmail'));
  };
  const handleToggleSelectTicket = (checked: boolean, id: string) => {
    console.debug(`handleToggleSelectTicket ${checked} ${id}`);
    dispatch(ticketGroupTableActions.setSelectedId(id, checked));
  };

  const wsLatestReceivedTimeWithData = useSelector(
    wsCurStatusInboxesLatestReceivedTimeWithDataSelector,
  );

  const renderGroupTable = (scrollRef) => {
    return (
      <SGroupTable
        className={
          !showTicket[ticketGroup.status] ? 'show' : 'collapsed'
        }
        onScroll={handleScroll}
        ref={scrollRef}
      >
        {!ticketGroup.isLoading && ticketGroup.isWsLoading && (
          <Box textAlign="center" margin="0.5rem">
            <SSiLoading>
              <AssignTicketLoading />
            </SSiLoading>
          </Box>
        )}
        {!ticketGroup.tickets.length
          ? renderNoTickets()
          : ticketGroup.tickets.map((ticket, index) => {
              const numIndex = index;
              const isLatestFromWs =
                ticket.fromWsAt &&
                ticket.fromWsAt === wsLatestReceivedTimeWithData;
              const eachTicketModel = new EachWorkdeskTicket(ticket);
              const labelsStringForDetail = ticket.labels
                ?.map((label) => label.name)
                .join(', ');
              const labelsExists = !!labelsStringForDetail;
              const canCopySubject = !!ticket.subject;
              return (
                <div key={ticket.id}>
                  {/* {(moreData = setDetailTicket(ticket))} */}
                  <STicketRow
                    className={`clickable ${
                      showMoreInfos[ticket.id] ? 'open' : ''
                    } ${
                      ticket.id === recentTicketId
                        ? 'is-just-opened'
                        : ''
                    } ${
                      ticket.isJustChanged
                        ? `is-just-changed-${ticket.isJustChanged.toLowerCase()}`
                        : ''
                    } ${isLatestFromWs ? 'latest-ws' : ''}`}
                    onClick={() => handleClickTicketRow(ticket.id)}
                    ref={rowRefs[ticket.id]}
                  >
                    {workdeskColumns.map((col, index) => (
                      <STicketCell
                        className={`${
                          showMoreInfos[ticket.id] ? 'open' : ''
                        }`}
                        key={`${col.dataKey}-${index}`}
                        width={col.width}
                        textAlign={col.textAlign}
                        onClick={() =>
                          index !== 0 &&
                          handleClickTicketRow(ticket.id)
                        }
                      >
                        {!col.renderCell
                          ? ticket[col.dataKey]
                          : col.renderCell({
                              value: ticket[col.dataKey],
                              dropdownId,
                              showMoreInfos,
                              handleDirect,
                              handleThreePoint,
                              handleAssign,
                              assignLoadingTicketIdMap: isAssignLoadingTicketIdMap,
                              showDropdownRef,
                              renderDropdown,
                              ticket,
                              handleSINonSI,
                              manualSiLoadingIdMap,
                              numIndex,
                              numTicket: ticketGroup.tickets.length,
                              handlePreviewEmailBody,
                              handleToggleSelectTicket,
                              isSelected:
                                (selectedId &&
                                  selectedId[ticket.id]) ||
                                false,
                              isShowMoreInfo:
                                showMoreInfos &&
                                showMoreInfos[ticket.id],
                              canSelectTicket,
                              canOpenTicket,
                              canAssignOrUnassignTicket,
                            })}
                      </STicketCell>
                    ))}
                  </STicketRow>

                  {/* expand detail */}
                  {showMoreInfos[ticket.id] && (
                    <SMoreDetailAndErrorWrap>
                      <SLabelsAndSubjectWrap>
                        {labelsExists && (
                          <SSubjectInMore>
                            <SLabelSubjectTitle>
                              Labels:
                            </SLabelSubjectTitle>
                            <SLabelSubjectValue
                              className="copyable"
                              onClick={() =>
                                handleCopy(labelsStringForDetail)
                              }
                            >
                              <GmailLabels
                                labels={eachTicketModel.ticket.labels}
                                showAllLabels
                              />
                              <SCopyIcon
                                className="clickable"
                                src={copyIcon}
                                onClick={() =>
                                  handleCopy(labelsStringForDetail)
                                }
                              />
                            </SLabelSubjectValue>
                          </SSubjectInMore>
                        )}
                        <SSubjectInMore>
                          <SLabelSubjectTitle>
                            Subject:
                          </SLabelSubjectTitle>
                          <SLabelSubjectValue
                            onClick={() =>
                              canCopySubject
                                ? handleCopy(
                                    eachTicketModel.ticket.subject,
                                  )
                                : null
                            }
                            className={
                              canCopySubject ? 'copyable' : ''
                            }
                          >
                            {dashIfEmpty(
                              eachTicketModel.ticket.subject,
                            )}
                            {canCopySubject && (
                              <SCopyIcon
                                className="clickable"
                                src={copyIcon}
                                onClick={() =>
                                  handleCopy(
                                    eachTicketModel.ticket.subject,
                                  )
                                }
                              />
                            )}
                          </SLabelSubjectValue>
                        </SSubjectInMore>
                      </SLabelsAndSubjectWrap>
                      <SMoreDetail>
                        {eachTicketModel
                          .getDetailTicket()
                          .map((detail) => [
                            !detail.hoverText ? (
                              <SEaceTitle
                                key={`title-${detail.title}`}
                              >
                                {detail.title}
                              </SEaceTitle>
                            ) : (
                              <Tooltip
                                title={detail.hoverText}
                                key={`title-${detail.title}`}
                              >
                                <SEaceTitle>
                                  {detail.title}
                                </SEaceTitle>
                              </Tooltip>
                            ),
                            <SEachDetail
                              key={`detail-${detail.title}`}
                              className={
                                detail.copyAble ? 'copyable' : ''
                              }
                              onClick={() =>
                                detail.copyAble &&
                                handleCopy(detail.detail)
                              }
                            >
                              {detail.detail}
                              {detail.copyAble && (
                                <SCopyIcon
                                  className="clickable"
                                  src={copyIcon}
                                  onClick={() =>
                                    handleCopy(detail.detail)
                                  }
                                />
                              )}
                            </SEachDetail>,
                          ])}
                      </SMoreDetail>
                      {eachTicketModel.shouldShowHasSubmissionMessages() && (
                        <SMoreDetailError>
                          <SMoreDetailTitle>
                            Submission Messages
                          </SMoreDetailTitle>
                          <SMoreDetailErrorList>
                            {eachTicketModel
                              .getTicketSubmissionResults()
                              .map((message, i) => (
                                <SEachFail
                                  key={`${message.bookingNo}-${i}`}
                                >
                                  {message.status === 'F' ? (
                                    <SErrorOutlineRounded fontSize="small" />
                                  ) : (
                                    <SCheckCircleOutlineRounded fontSize="small" />
                                  )}
                                  <SFailBookingNo>
                                    {dashIfEmpty(message.bookingNo)}
                                  </SFailBookingNo>
                                  <SFailMsg>{message.msg}</SFailMsg>
                                </SEachFail>
                              ))}
                          </SMoreDetailErrorList>
                        </SMoreDetailError>
                      )}
                    </SMoreDetailAndErrorWrap>
                  )}
                </div>
              );
            })}
        {ticketGroup.isLoading && (
          <Box textAlign="center" margin="0.5rem">
            <CircularProgress size="1.2rem" />
            {envCheckers.isLocalOrDev() && (
              <STextStartAt>
                {statusesStartAt &&
                  statusesStartAt[selectedStatus] &&
                  statusesStartAt[selectedStatus][
                    selectedTicketType
                  ] &&
                  `Loading with offset ${statusesStartAt[selectedStatus][selectedTicketType]} with ${ROWS_PER_STATUS} rows limit`}
              </STextStartAt>
            )}
          </Box>
        )}
      </SGroupTable>
    );
  };
  return (
    <STicketGroupWrapper key={ticketGroup.status}>
      {hasRecentTicketId && (
        <SFindRecentTicketWrapper>
          <Tooltip title="Find the recently opened ticket row">
            <SFindRecentFab
              size="small"
              onClick={() => handleFindRecentTicketrow()}
            >
              <YoutubeSearchedForRounded />
            </SFindRecentFab>
          </Tooltip>
        </SFindRecentTicketWrapper>
      )}
      {isScrollable && (
        <SScrollTopBotWrapper>
          <Tooltip title="Scroll to bottom">
            <SLightGreyFab
              size="small"
              onClick={() => scrollToBottom()}
            >
              <KeyboardArrowDownRounded />
            </SLightGreyFab>
          </Tooltip>
        </SScrollTopBotWrapper>
      )}
      {isScrollable && showBackToTopBtn && (
        <SSCrollTopWrapper>
          <Tooltip title="Back to top">
            <SLightGreyFab size="small" onClick={() => scrollToTop()}>
              <KeyboardArrowUpRounded />
            </SLightGreyFab>
          </Tooltip>
        </SSCrollTopWrapper>
      )}
      <SGroupHeader>
        {workdeskColumns.map((col, index) => (
          <STicketCellHeader
            key={`${col.dataKey}-${index}`}
            width={col.width}
            className={index === 0 ? 'no-padding-left' : ''}
          >
            {index === 0 ? (
              <SStatusAndBulkActionBar>
                <SStatus>
                  <SGroupAndCount>
                    <SGroupName>
                      {/* <MatUiCheckbox /> */}
                      <SelectAllCheckbox />
                      <SGroupIcon>
                        {<ticketGroup.StatusIconClass />}
                      </SGroupIcon>
                      {ticketGroup.statusLabel}
                      <SHeadArrow
                        className="clickable"
                        src={
                          !showTicket[ticketGroup.status]
                            ? headArrow
                            : headArrowDown
                        }
                        onClick={() =>
                          handleToggleShowGroup(ticketGroup.status)
                        }
                      />
                    </SGroupName>
                    {ticketGroup.tickets.length > 0 && (
                      <SLoadedCount>
                        {`(${ticketGroup.tickets.length} loaded)`}
                      </SLoadedCount>
                    )}
                  </SGroupAndCount>
                  <SStatusColor
                    statusColor={ticketGroup.statusColor}
                  />
                </SStatus>
                <BulkActionBar />
              </SStatusAndBulkActionBar>
            ) : (
              renderLabelWithSortDir(col)
            )}
          </STicketCellHeader>
        ))}
      </SGroupHeader>
      {showTicket[ticketGroup.status] && <SLine />}
      <BottomScrollListener onBottom={hitBottomCallback} offset={10}>
        {(scrollRef) => renderGroupTable(scrollRef)}
      </BottomScrollListener>
    </STicketGroupWrapper>
  );
};

export default TicketGroupTable;
const SSiLoading = styled.div`
  & > svg {
    width: 25px;
    height: 25px;
  }
`;

const SDropdownLine = styled.div`
  border: 1px solid #f0f0f0;
`;

const SDropdownIcon = styled.div`
  height: 10px;
  margin-right: 10px;
`;

const STextChoice = styled.div`
  display: flex;
  margin-left: 13px;
`;

const SEachChoice = styled.div`
  display: flex;
  flex-direction: column;
  color: ${(props) => props.theme.palette.primary.darkGrey};
  line-height: 35px;
  font-size: 14px;
  width: 160px;
  justify-content: center;
  &:hover {
    cursor: pointer;
    background: ${(props) => props.theme.palette.primary.main};
    color: white;
    &.order1 {
      border-radius: 5px 5px 0px 0px;
    }
    &.order2 {
      border-radius: 0px 0px 5px 5px;
    }
    & > div > div > svg > path {
      fill: white;
    }
  }
`;
const SMoreDropdown = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;

  &.last {
    position: absolute;
    margin-top: -115px;
  }
  &.lessTicket {
    position: fixed;
    margin-right: 100px;
    margin-top: -7px;
  }
  background: #ffffff;
  box-shadow: 0px 6px 20px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
  width: 160px;
  right: 55px;
  margin-top: -2vh;

  justify-content: center;
  align-items: center;
  z-index: 1;
`;

const SLine = styled.div`
  border: 1px solid ${(props) => props.theme.palette.primary.main};
`;
const SStatus = styled.div`
  display: flex;
  flex-direction: column;
`;
const SStatusColor = styled.div`
  background: ${(props: { statusColor: string }) =>
    props.statusColor};
  height: 6px;
  box-shadow: inset 0px -1px 4px rgba(0, 0, 0, 0.25);
`;
const SHeadArrow = styled.img`
  margin-left: 0.2rem;
  padding: 0 0.3rem 0 0.5rem;
  z-index: 1;
  cursor: pointer;
`;

const SMoreDetail = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr 100px 1fr 100px 1fr;
  grid-row-gap: 0.5rem;
`;
const SEaceTitle = styled.div`
  font-weight: ${(props) => props.theme.typography.fontWeight.title};
`;

const SCopyIcon = styled.img`
  position: absolute;
  margin-left: 0.5rem;
  margin-right: 0.5rem;
  opacity: 0;
`;
const SEachDetail = styled.div`
  position: relative;
  word-break: break-word;
  &:hover {
    &.copyable {
      text-decoration-line: underline;
    }
  }
  &:hover > ${SCopyIcon} {
    opacity: 100%;
    transition: opacity 0.15s ease-in;
  }
`;

const STicketGroupWrapper = styled.div`
  /* margin-bottom: 1rem; */
`;

const SGroupName = styled.div`
  color: ${(props) => props.theme.palette.primary.darkGrey};
  font-weight: 700;
  font-size: 18px;
  background: ${(props) => props.theme.palette.background.paper};
  padding: 0.4rem 0.3rem;
  border-radius: 10px 10px 0 0;
  display: inline-flex;
  width: max-content;
  align-items: center;
  box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.25);
`;
const SGroupIcon = styled.div`
  margin-right: 0.5rem;
  display: inline-flex;
  align-items: center;
`;

const SGroupHeader = styled.div`
  display: flex;
`;
const SGroupTable = styled.div`
  box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.25);
  background: white;
  &.collapsed {
    display: none;
  }
  max-height: 68vh;
  overflow-y: auto;
  &::-webkit-scrollbar {
    display: none;
  }
`;
const STicketRow = styled.div.attrs((props) => ({
  className: props.className,
}))`
  display: flex;
  position: relative;
  // height:100px;
  align-items: center;
  border-bottom: 1px solid ${(props) => props.theme.palette.grey[300]};
  &.latest-ws {
    background: ${ticketLabelColorMap.fromWs};
  }
  &.open {
    background: ${(props) => props.theme.palette.primary.main};
  }
  &.is-just-changed-new:not(.open) {
    background: ${ticketLabelColorMap.justChanged};
  }
  &.is-just-changed-loaded:not(.open) {
    background: ${ticketLabelColorMap.changedButLoaded};
  }
  &.is-just-opened:not(.open) {
    background: ${ticketLabelColorMap.justOpened};
  }
`;
const STicketCell = styled.div`
  width: ${(props: { width?: string; textAlign?: string }) =>
    props.width};
  // padding: 0 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: ${(props: { width?: string; textAlign?: string }) =>
    props.textAlign || 'center'};
  &.open {
    color: white;
  }
`;
const STicketCellHeader = styled.div.attrs((props) => ({
  className: props.className,
}))`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${(props: { width: string }) => props.width};
  // padding: 0 10px;
  &.no-padding-left {
    // padding-right: 12px;
    padding-right: 1px;
    padding-left: 0;
    justify-content: unset;
  }
`;

const SNoTickets = styled.div`
  line-height: 2;
  text-align: center;
  color: ${(props) => props.theme.palette.grey[500]};
`;

const SSortLabelWithDir = styled.div`
  display: flex;
`;
const SSortLabel = styled.div`
  text-align: right;
`;
const SSortDir = styled.div`
  // margin: 0 4px;
`;
const SSortDirAndRank = styled.div`
  display: flex;
  align-items: center;
`;
const SSortUpdown = styled(Sort)`
  color: ${(props) => props.theme.palette.grey[400]};
  margin-left: 5px;
`;

const SMarkSiDropIcon = styled(Check)`
  &.MuiSvgIcon-root {
    font-size: 12px;
  }
`;
const SMarkNSiDropIcon = styled(Close)`
  &.MuiSvgIcon-root {
    font-size: 12px;
  }
`;
const SStatusAndBulkActionBar = styled.div`
  display: flex;
  column-gap: 4px;
  align-items: baseline;
`;
const STextStartAt = styled.div`
  font-size: ${(props) => props.theme.typography.fontSize.extraSmall};
  color: ${(props) => props.theme.palette.text.secondary};
`;
const SLoadedCount = styled.div`
  font-size: ${(props) => props.theme.typography.fontSize.extraSmall};
  color: ${(props) => props.theme.palette.text.secondary};
  position: absolute;
  right: -65px;
  top: 0;
  margin-top: 15px;
`;

const SGroupAndCount = styled.div`
  position: relative;
`;
const SMoreDetailAndErrorWrap = styled.div`
  line-height: 1.53;
  // height: 20vh;
  background: rgba(184, 93, 145, 0.2);
  padding: 10px;
`;
const SMoreDetailError = styled.div`
  margin-top: 1rem;
  border-top: solid 1px ${(props) => props.theme.palette.grey[400]};
`;
const SEachFail = styled.div`
  display: grid;
  grid-template-columns: auto 120px auto;
  column-gap: 1rem;
  line-height: 2;
  align-items: center;
  justify-content: flex-start;
`;
const SFailMsg = styled.div``;
const SFailBookingNo = styled.div`
  font-weight: ${(props) => props.theme.typography.fontWeight.title};
`;
const SMoreDetailTitle = styled.div`
  line-height: 3;
  font-weight: ${(props) => props.theme.typography.fontWeight.title};
  display: flex;
  align-items: center;
`;
const SMoreDetailErrorList = styled.div``;

const SSCrollTopWrapper = styled.div`
  z-index: 1;
  position: fixed;
  bottom: 2rem;
  right: 4.3rem;
`;
const SFindRecentTicketWrapper = styled.div`
  z-index: 1;
  position: fixed;
  top: 7.8rem;
  right: 4.3rem;
`;
const SScrollTopBotWrapper = styled.div`
  z-index: 1;
  position: fixed;
  top: 10.5rem;
  right: 4.3rem;
`;
const SFindRecentFab = styled(Fab)`
  &.MuiFab-root {
    background: #ddebff;
    border: solid 1px #6db5ff;
    color: #207cff;
    &:hover {
      background: #cfe3ff;
    }
  }
`;

const SLightGreyFab = styled(Fab)`
  &.MuiFab-root {
    color: #444444;
    background-color: #e2e2e2;
    border: solid 1px #777777;
    &:hover {
      background: #d0d0d0;
    }
  }
`;
const SCheckCircleOutlineRounded = styled(CheckCircleOutlineRounded)`
  color: ${(props) => props.theme.palette.success.main};
`;
const SErrorOutlineRounded = styled(ErrorOutlineRounded)`
  color: ${(props) => props.theme.palette.error.main};
`;
const SLabelsAndSubjectWrap = styled.div`
  margin-bottom: 0.5rem;
  display: grid;
  row-gap: 3px;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${(props) => props.theme.palette.grey[400]};
`;
const SSubjectInMore = styled.div`
  display: grid;
  grid-template-columns: 4rem auto;
`;
const SLabelSubjectTitle = styled.div`
  font-weight: ${(props) => props.theme.typography.fontWeight.title};
`;
const SLabelSubjectValue = styled.div.attrs((props) => ({
  className: props.className,
}))`
  position: relative;
  word-break: break-word;
  &.copyable:hover {
    text-decoration-line: underline;
    & ${SGmailLabel} {
      text-decoration-line: underline;
    }
  }
  &:hover > ${SCopyIcon} {
    opacity: 100%;
    transition: opacity 0.15s ease-in;
  }
`;
